import React, { useContext } from "react";
import { EventGalleryContext } from "./index";
import { FindPhotosGalleryContext } from "../MasterImageSearch/components/results/PhotosContainer";
import Telemetry from "../../utils/rudderstackAnalytics";
import _ from "lodash";
import PhotoPricing from "./PhotoPricing";
import EndUserDownloadLink from "./EndUserDownloadLink";
import DownloadWithEmailButton from "./DownloadWithEmailButton";
import { OrgDownloadContext } from "../OrganizationPhotos/OrgDownloadContext";
import ViewCartLink from "./ViewCartLink";
import RemoveFromCartButton from "./RemoveFromCartButton";
import { Photo, Event } from "./types";
import AddToCartButton from "./AddToCartButton";
import { securedEventPath } from "../../utils/pathUtils";
import ContrastForegroundUtility from "../../utils/ContrastForegroundUtility";

const LightBoxHeader = ({
  photo,
  primaryColor,
  trackTelemetry,
  parentName,
  canShowGuestEmailModal,
  collectEmailModal,
  securedOrgView,
  userId
}: {
  photo: any;
  primaryColor?: string;
  trackTelemetry: boolean;
  parentName?: string;
  canShowGuestEmailModal: (photo: any) => void;
  collectEmailModal: () => void;
  securedOrgView?: boolean;
  userId?: number;
}) => {
  const eventGalleryContext = useContext(EventGalleryContext);
  const findPhotosContext = useContext(FindPhotosGalleryContext);
  const orgDownloadContext = useContext(OrgDownloadContext);
  const isSecuredFaceSearch = orgDownloadContext?.fromSecuredFaceSearch;

  const event = eventGalleryContext.event
    ? eventGalleryContext.event
    : photo.album.event;
  const freeEvent = () => event && event.price === 0;
  const cartPhotos =
    eventGalleryContext.cart?.photos ?? findPhotosContext.cart?.photos ?? [];
  const cartHasPhotos = cartPhotos.length > 0;
  const cartId = eventGalleryContext.cart
    ? eventGalleryContext.cart.id
    : findPhotosContext.cart
    ? findPhotosContext.cart.id
    : null;
  const isEmbed = eventGalleryContext.isEmbed
    ? eventGalleryContext.isEmbed
    : findPhotosContext.isEmbed;

  const colorTheme = primaryColor
    ? primaryColor
    : eventGalleryContext.pageConfiguration
    ? eventGalleryContext.pageConfiguration.primaryColor
    : "#259FA5";

  const anyUserId =
    eventGalleryContext?.userId || eventGalleryContext?.guestUserId || userId;
  const isAuthorizedGallery =
    anyUserId || eventGalleryContext?.securedGalleryView || securedOrgView;

  const selectedUserId =
    eventGalleryContext?.guestUserId || eventGalleryContext?.userId || userId;
  const href = `/end_user_content_downloads?photo_ids=${photo.id}${
    selectedUserId ? `&user_id=${selectedUserId}` : ""
  }`;
  const directDownloadEnabled =
    eventGalleryContext.directDownloadEnabled ||
    orgDownloadContext?.directDownloadEnabled ||
    findPhotosContext?.directDownloadEnabled;

  const trackRemoveFromCart = () => {
    if (trackTelemetry) {
      Telemetry.track("event-gallery:remove-from-cart", {
        eventId: event.id,
        eventName: event.name,
        photoId: photo.id,
        cartId: cartId,
        source: "lightbox",
        embeddedMode: isEmbed
      });
    }
  };

  const trackAddToCart = () => {
    if (trackTelemetry) {
      Telemetry.track("event-gallery:add-to-cart", {
        eventId: event.id,
        eventName: event.name,
        photoId: photo.id,
        cartId: cartId,
        source: "lightbox",
        embeddedMode: isEmbed
      });
    }
  };

  const onRemoveFromCart = (photo: Photo) => {
    trackRemoveFromCart();
    eventGalleryContext.cart
      ? eventGalleryContext.removePhotoFromCart(photo)
      : // @ts-ignore (FindPhotosGallery is removed from Platform, this needs to be removed from the codebase)
        findPhotosContext.removePhotoFromCart(photo);
  };

  const onAddToCart = (photo: Photo) => {
    trackAddToCart();
    if (eventGalleryContext.canShowEmailPopup()) {
      canShowGuestEmailModal(photo);
    } else if (eventGalleryContext.event) {
      eventGalleryContext.addPhotoToCart(photo);
    } else {
      // @ts-ignore
      findPhotosContext.addPhotoToCart(photo);
    }
  };

  const viewEventGalleryBtnStyles: React.CSSProperties = {
    color: primaryColor
      ? ContrastForegroundUtility.fgColor(primaryColor)
      : "#fff",
    background: primaryColor || "#ff0072",
    textAlign: "center",
    marginTop: "10px"
  };

  const renderPhotoInteractionSection = () => {
    if (freeEvent() && directDownloadEnabled) {
      return isAuthorizedGallery ? (
        <EndUserDownloadLink href={href} colorTheme={colorTheme} />
      ) : (
        <DownloadWithEmailButton
          colorTheme={colorTheme}
          collectEmailModal={collectEmailModal}
        />
      );
    }

    if (isSecuredFaceSearch && directDownloadEnabled) {
      // TODO: Add View Event Gallery link
      return (
        <a
          style={viewEventGalleryBtnStyles}
          className="geo-btn geo-btn--caps"
          href={securedEventPath({
            browsePhotosPath: event.browsePhotosPath,
            organizerUserName: orgDownloadContext?.organizerUserName,
            embedMode: isEmbed,
            cartId: cartId,
            secureAccessToken: orgDownloadContext?.secureAccessToken
          })}
        >
          {securedOrgView ? (
            <span>
              {" "}
              Purchase from event
              <span className="fa fa-angle-right geo-eve-name-arrow" />
            </span>
          ) : (
            "View Event Gallery"
          )}
        </a>
      );
    }

    return (
      <>
        {photo.isSelected && (
          <ViewCartLink
            cartId={cartId}
            isEmbed={isEmbed}
            parentName={parentName || ""}
            colorTheme={colorTheme}
          />
        )}
        {photo.isSelected ? (
          <RemoveFromCartButton
            colorTheme={colorTheme}
            photo={photo}
            onRemoveFromCart={onRemoveFromCart}
          />
        ) : (
          <AddToCartButton
            colorTheme={colorTheme}
            photo={photo}
            onAddToCart={onAddToCart}
          />
        )}
      </>
    );
  };

  return (
    <React.Fragment>
      <div className="modal-footer-details egm-lb-footer-cta">
        <PhotoPricing event={event} photo={photo} freeEvent={freeEvent()} />
        <div className="egm-cart-btns">{renderPhotoInteractionSection()}</div>
      </div>
      <div className="modal-footer-actions"></div>
    </React.Fragment>
  );
};

export default LightBoxHeader;
